import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getCourseCompletions, verifyCourseCompletion } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';

const CourseCompletions = () => {
  const { isMobile } = useWindowSize();
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('submitted');
  const [selectedCompletion, setSelectedCompletion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [verifyForm, setVerifyForm] = useState({ status: 'approved', adminNotes: '', rejectionReason: '' });

  useEffect(() => { fetchCompletions(); }, []);

  const fetchCompletions = async () => {
    try {
      const res = await getCourseCompletions();
      setCompletions(res.data.completions || res.data.data || []);
    } catch (error) {
      console.log('Error fetching completions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      await verifyCourseCompletion(selectedCompletion._id, verifyForm);
      setShowModal(false);
      setSelectedCompletion(null);
      fetchCompletions();
    } catch (error) {
      alert('Error verifying completion');
    }
  };

  const filtered = completions.filter(c => filter === 'all' ? true : c.status === filter);

  const statusColors = {
    submitted: { bg: '#eaf4fb', color: '#2980b9' },
    under_review: { bg: '#fef9e7', color: '#d4ac0d' },
    approved: { bg: '#eafaf1', color: '#27ae60' },
    rejected: { bg: '#fdf0f0', color: '#e74c3c' }
  };

  const categoryColors = {
    healing: '#4a8f82',
    spiritual: '#c9a84c',
    prosperity: '#8DC63F'
  };

  const s = {
    layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif", overflowX: 'hidden' },
    main: { marginLeft: isMobile ? 0 : '260px', flex: 1, padding: isMobile ? '64px 16px 24px' : '32px', minWidth: 0, overflowX: 'hidden' },
    header: { marginBottom: '20px' },
    headerTitle: { fontFamily: "'Raleway', sans-serif", fontSize: isMobile ? '22px' : '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
    headerSub: { fontSize: '13px', color: '#666', margin: 0 },

    // Tabs — horizontal scroll on mobile (width: 100% + overflowX: auto keeps it contained)
    tabsWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '20px' },
    tabs: { display: 'inline-flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', minWidth: 'min-content' },
    tab: { padding: isMobile ? '8px 14px' : '10px 16px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
    tabActive: { background: '#4a8f82', color: '#fff' },
    tabCount: { background: 'rgba(0,0,0,0.1)', borderRadius: '10px', padding: '1px 6px', fontSize: '10px' },

    // Card list (mobile)
    cardList: { display: 'flex', flexDirection: 'column', gap: '12px' },
    card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '16px' },
    cardName: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '15px', color: '#1a1a2e', marginBottom: '2px' },
    cardEmail: { fontSize: '12px', color: '#999', marginBottom: '12px' },
    cardRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '13px' },
    cardLabel: { color: '#888', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' },
    cardValue: { color: '#333', fontWeight: '600' },
    cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' },

    // Table (desktop)
    tableWrap: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
    tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' },
    tableTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
    table: { width: '100%', borderCollapse: 'collapse' },
    tableHead: { background: '#f9faf7' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', fontFamily: "'Raleway', sans-serif", borderBottom: '1px solid #e0e0e0' },
    tableRow: { borderBottom: '1px solid #f5f5f5' },
    td: { padding: '14px 16px', fontSize: '13px', color: '#333', verticalAlign: 'middle' },
    tdTitle: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
    tdSub: { fontSize: '11px', color: '#999' },

    badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px', display: 'inline-block' },
    actionBtn: { background: 'none', border: '1px solid #4a8f82', color: '#4a8f82', borderRadius: '4px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
    empty: { padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' },

    // Modal
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000 },
    modalCard: { background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : '10px', padding: isMobile ? '24px 20px' : '36px', width: '100%', maxWidth: isMobile ? '100%' : '520px', maxHeight: isMobile ? '90vh' : '85vh', overflowY: 'auto' },
    modalTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#1a1a2e', marginBottom: '16px' },
    modalInfo: { background: '#f9faf7', borderRadius: '8px', padding: '14px', marginBottom: '20px', fontSize: '13px', color: '#333', display: 'flex', flexDirection: 'column', gap: '6px' },
    modalBtns: { display: 'flex', gap: '10px', marginTop: '8px' },
    field: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
    input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
    submitBtn: { flex: 1, color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
    cancelBtn: { flex: 1, background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' }
  };

  const openReview = (comp) => { setSelectedCompletion(comp); setShowModal(true); };

  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={s.main}>

        <div style={s.header}>
          <h1 style={s.headerTitle}>Course Completions</h1>
          <p style={s.headerSub}>Review and verify student course completion submissions</p>
        </div>

        {/* Filter Tabs */}
        <div style={s.tabsWrap}>
          <div style={s.tabs}>
            {['submitted', 'under_review', 'approved', 'rejected', 'all'].map(tab => (
              <button key={tab} onClick={() => setFilter(tab)}
                style={{ ...s.tab, ...(filter === tab ? s.tabActive : {}) }}>
                {tab.replace('_', ' ').toUpperCase()}
                <span style={s.tabCount}>
                  {tab === 'all' ? completions.length : completions.filter(c => c.status === tab).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Verify Modal */}
        {showModal && selectedCompletion && (
          <div style={s.modalOverlay} onClick={function(e) { if (e.target === e.currentTarget) { setShowModal(false); setSelectedCompletion(null); } }}>
            <div style={s.modalCard}>
              <div style={s.modalTitle}>Verify Course Completion</div>
              <div style={s.modalInfo}>
                <div><strong>Student:</strong> {selectedCompletion.student?.firstName} {selectedCompletion.student?.lastName}</div>
                <div><strong>Course:</strong> {selectedCompletion.courseName}</div>
                <div><strong>Category:</strong> {selectedCompletion.courseCategory}</div>
                <div><strong>Completion Date:</strong> {new Date(selectedCompletion.completionDate).toLocaleDateString('en-IN')}</div>
                {selectedCompletion.certificateNumber && (
                  <div><strong>Certificate No:</strong> {selectedCompletion.certificateNumber}</div>
                )}
              </div>
              <form onSubmit={handleVerify}>
                <div style={s.field}>
                  <label style={s.label}>Decision</label>
                  <select style={s.input} value={verifyForm.status}
                    onChange={e => setVerifyForm({ ...verifyForm, status: e.target.value })}>
                    <option value="approved">Approve</option>
                    <option value="rejected">Reject</option>
                    <option value="under_review">Mark Under Review</option>
                  </select>
                </div>
                {verifyForm.status === 'rejected' && (
                  <div style={s.field}>
                    <label style={s.label}>Rejection Reason</label>
                    <textarea style={{ ...s.input, height: '80px' }} value={verifyForm.rejectionReason}
                      onChange={e => setVerifyForm({ ...verifyForm, rejectionReason: e.target.value })}
                      placeholder="Reason for rejection..." required />
                  </div>
                )}
                <div style={s.field}>
                  <label style={s.label}>Admin Notes (optional)</label>
                  <textarea style={{ ...s.input, height: '80px' }} value={verifyForm.adminNotes}
                    onChange={e => setVerifyForm({ ...verifyForm, adminNotes: e.target.value })}
                    placeholder="Internal notes..." />
                </div>
                <div style={s.modalBtns}>
                  <button type="submit" style={{ ...s.submitBtn, background: verifyForm.status === 'approved' ? '#27ae60' : verifyForm.status === 'rejected' ? '#e74c3c' : '#4a8f82' }}>
                    {verifyForm.status === 'approved' ? 'Approve' : verifyForm.status === 'rejected' ? 'Reject' : 'Save'}
                  </button>
                  <button type="button" onClick={() => { setShowModal(false); setSelectedCompletion(null); }} style={s.cancelBtn}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div style={s.empty}>Loading completions...</div>
        ) : filtered.length === 0 ? (
          <div style={{ ...s.empty, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
            No {filter.replace('_', ' ')} submissions found.
          </div>
        ) : isMobile ? (

          /* ── Mobile: Card list ── */
          <div>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              {filtered.length} submission{filtered.length !== 1 ? 's' : ''}
            </div>
            <div style={s.cardList}>
              {filtered.map(comp => (
                <div key={comp._id} style={s.card}>
                  <div style={s.cardName}>{comp.student?.firstName} {comp.student?.lastName}</div>
                  <div style={s.cardEmail}>{comp.student?.email}</div>

                  <div style={s.cardRow}>
                    <span style={s.cardLabel}>Course</span>
                    <span style={{ ...s.cardValue, maxWidth: '55%', textAlign: 'right', fontSize: '12px' }}>{comp.courseName}</span>
                  </div>
                  <div style={s.cardRow}>
                    <span style={s.cardLabel}>Category</span>
                    <span style={{ ...s.badge, background: `${categoryColors[comp.courseCategory]}20`, color: categoryColors[comp.courseCategory] }}>
                      {comp.courseCategory}
                    </span>
                  </div>
                  <div style={s.cardRow}>
                    <span style={s.cardLabel}>Completed</span>
                    <span style={s.cardValue}>{new Date(comp.completionDate).toLocaleDateString('en-IN')}</span>
                  </div>
                  {comp.certificateNumber && (
                    <div style={s.cardRow}>
                      <span style={s.cardLabel}>Certificate</span>
                      <span style={s.cardValue}>{comp.certificateNumber}</span>
                    </div>
                  )}

                  <div style={s.cardFooter}>
                    <span style={{ ...s.badge, background: statusColors[comp.status]?.bg, color: statusColors[comp.status]?.color }}>
                      {comp.status?.replace('_', ' ')}
                    </span>
                    {(comp.status === 'submitted' || comp.status === 'under_review') && (
                      <button onClick={() => openReview(comp)} style={s.actionBtn}>Review</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        ) : (

          /* ── Desktop: Table ── */
          <div style={s.tableWrap}>
            <div style={s.tableHeader}>
              <div style={s.tableTitle}>
                {filter === 'all' ? 'All' : filter.replace('_', ' ')} Submissions ({filtered.length})
              </div>
            </div>
            <table style={s.table}>
              <thead>
                <tr style={s.tableHead}>
                  <th style={s.th}>Student</th>
                  <th style={s.th}>Course</th>
                  <th style={s.th}>Category</th>
                  <th style={s.th}>Completion Date</th>
                  <th style={s.th}>Certificate No</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(comp => (
                  <tr key={comp._id} style={s.tableRow}>
                    <td style={s.td}>
                      <div style={s.tdTitle}>{comp.student?.firstName} {comp.student?.lastName}</div>
                      <div style={s.tdSub}>{comp.student?.email}</div>
                    </td>
                    <td style={s.td}><div style={s.tdTitle}>{comp.courseName}</div></td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: `${categoryColors[comp.courseCategory]}20`, color: categoryColors[comp.courseCategory] }}>
                        {comp.courseCategory}
                      </span>
                    </td>
                    <td style={s.td}>{new Date(comp.completionDate).toLocaleDateString('en-IN')}</td>
                    <td style={s.td}>{comp.certificateNumber || '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: statusColors[comp.status]?.bg, color: statusColors[comp.status]?.color }}>
                        {comp.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={s.td}>
                      {comp.status === 'submitted' || comp.status === 'under_review' ? (
                        <button onClick={() => openReview(comp)} style={s.actionBtn}>Review</button>
                      ) : (
                        <span style={s.tdSub}>{comp.status === 'approved' ? 'Verified' : 'Closed'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default CourseCompletions;
